import { Link, type LinkProps } from '@remix-run/react'
import { type VariantProps } from 'class-variance-authority'
import { forwardRef } from 'react'
import { isNativePlatform, openExternalUrl } from '#app/utils/external-links.js'
import { cn } from '#app/utils/misc.js'
import { Button, type buttonVariants } from './ui/button'
import { Icon, type IconName } from './ui/icon'
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip'

export const NavListItemDefaultClassName =
	'block rounded-md p-2 transition-colors hover:bg-accent hover:text-accent-foreground '

export type DynamicNavLink = {
	to: string
	label: string
	icon?: IconName
	end?: boolean
	items?: DynamicNavLink[]
}

// dev wanting to add language to links
// https://discord.com/channels/770287896669978684/770287896669978687/1301961834282094703
export const PageLink = forwardRef(function PageLinkk(
	props: LinkProps & React.ComponentPropsWithoutRef<'a'>,
	ref: React.ForwardedRef<HTMLAnchorElement>,
) {
	const { className, prefetch = 'intent', ...rest } = props
	return (
		<Link
			{...rest}
			prefetch={prefetch}
			ref={ref}
			className={cn('font-medium text-link', className)}
		/>
	)
})
PageLink.displayName = 'PageLink'

export const ButtonIconLink = forwardRef<
	HTMLButtonElement,
	React.ComponentProps<'button'> & {
		to: string
		label?: string
		relative?: 'path' | 'route'
		variant?: VariantProps<typeof buttonVariants>['variant']
		size?: VariantProps<typeof buttonVariants>['size']
		icon?: IconName
		smIconOnly?: boolean
		tooltip?: string | React.ComponentProps<typeof TooltipContent>
	}
>(
	(
		{
			to,
			relative = 'route',
			label,
			variant = 'outline',
			size = 'default',
			icon,
			smIconOnly = false,
			tooltip,
			className,
			...props
		},
		ref,
	) => {
		const link = (
			<Link to={to} prefetch="intent" relative={relative}>
				{icon ? (
					<Icon name={icon}>
						<span className={cn(smIconOnly && 'sr-only md:not-sr-only')}>
							{label}
						</span>
					</Icon>
				) : (
					<span>{label}</span>
				)}
			</Link>
		)

		const button = (
			<Button
				ref={ref}
				variant={variant}
				size={size}
				className={className}
				asChild
				{...props}
			>
				{link}
			</Button>
		)

		if (!tooltip) {
			return button
		}

		if (typeof tooltip === 'string') {
			tooltip = {
				children: tooltip,
			}
		}

		return (
			<Tooltip>
				<TooltipTrigger asChild>{button}</TooltipTrigger>
				<TooltipContent
					side="right"
					align="center"
					hidden={true}
					{...tooltip}
				/>
			</Tooltip>
		)
	},
)
ButtonIconLink.displayName = 'ButtonIconLink'

export const CardButtonLink = ({
	to,
	label,
	icon,
	srText,
	relative = 'route',
	buttonVariant = 'outline',
	className,
}: {
	to: string
	label?: string
	icon?: IconName
	srText?: string
	relative?: 'path' | 'route'
	className?: string
	buttonVariant?: VariantProps<typeof buttonVariants>['variant']
}) => {
	return (
		<Button variant={buttonVariant} className={className} asChild>
			<Link to={to} prefetch="intent" relative={relative}>
				{icon ? (
					<Icon name={icon}>
						{label && <span>{label}</span>}
						{srText && <span className="sr-only">{srText}</span>}
					</Icon>
				) : (
					<span>{label ?? 'View'}</span>
				)}
			</Link>
		</Button>
	)
}

export const BackLink = () => (
	<CardButtonLink to=".." label="Back" icon="arrow-left" />
)

export const CancelBackLink = ({ className }: { className?: string }) => (
	<Button variant="secondary" className={className} asChild>
		<Link to="..">Cancel</Link>
	</Button>
)

// https://remix.run/docs/en/main/components/link#relative
// .../id/edit -> ../id
export const EditorBackLink = ({ className }: { className?: string }) => (
	<CardButtonLink
		to=".."
		label="Back"
		icon="arrow-left"
		relative="path"
		className={className}
	/>
)

export const EntityNewLink = ({ entityName }: { entityName?: string }) => (
	<CardButtonLink
		to="new"
		label={entityName ? `New ${entityName}` : 'New'}
		icon="plus"
	/>
)

export const EntityEditLink = ({ entityName }: { entityName?: string }) => (
	<CardButtonLink
		to="edit"
		label={entityName ? `Edit ${entityName}` : 'Edit'}
		icon="pencil-1"
	/>
)

export const EntityViewLink = ({
	to,
	label,
}: {
	to: string
	label?: string
}) => <CardButtonLink to={to} label={label ?? 'View'} icon="eye-open" />

export const EntityImportLink = ({ entityName }: { entityName?: string }) => (
	<CardButtonLink
		to="import"
		label={entityName ? `Import ${entityName}` : 'Import'}
		icon="link-2"
	/>
)

export const ExternalLink = ({
	url,
	label,
	className,
	children,
}: {
	url: string
	label?: string
	className?: string
	children?: React.ReactNode
}) => {
	const content = children ?? label ?? url
	return (
		<a
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			onClick={(e) => {
				if (isNativePlatform()) {
					e.preventDefault()
					void openExternalUrl(url)
				}
			}}
			className={cn(
				'inline-flex items-center gap-1 text-link hover:underline',
				className,
			)}
		>
			{content}
			<Icon name="external-link" className="text-body-xs" />
		</a>
	)
}

/**
 * Button styled like {@link CardButtonLink} but opens an external URL (system
 * browser on native). Use in entity-card action lists for external links.
 */
export const CardButtonExternalLink = ({
	url,
	label,
	icon,
	srText,
	buttonVariant = 'outline',
	className,
}: {
	url: string
	label?: string
	icon?: IconName
	srText?: string
	className?: string
	buttonVariant?: VariantProps<typeof buttonVariants>['variant']
}) => {
	return (
		<Button
			variant={buttonVariant}
			className={className}
			onClick={() => void openExternalUrl(url)}
		>
			{icon ? (
				<Icon name={icon}>
					{label && <span>{label}</span>}
					{srText && <span className="sr-only">{srText}</span>}
				</Icon>
			) : (
				<span>{label ?? 'View'}</span>
			)}
		</Button>
	)
}
